import React, { type ChangeEvent, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import Text from '@ingka/text';
import { Checkbox } from '@ingka/checkbox';
import Button from '@ingka/button';
import designTokens from '@ingka/variables/design-tokens';
import ErrorSummary from '../components/ErrorSummary';
import PageContent from '../components/PageContent';
import coworkerImage from '../assets/coworker.jpg';
import {
  type FileAnswer,
  type Question,
  type QuestionType,
} from '../services/additionalInfo/model';
import QuestionsListItem from '../components/QuestionListItem';
import { useQuestions } from '../hooks/useQuestions';
import { mediaQueries } from '../styles/mediaQueries';
import FormField from '@ingka/form-field';
import {
  CHARACTER_LIMIT,
  ERROR_ID,
  VALIDATORS,
} from '../helpers/questionsHelper';
import StickyFooter from '../components/StickyFooter';
import StickyHeader from '../components/StickyHeader';
import { StyledWrapper } from '../components/PageLayout';
import colors from '../styles/colors';
import { ViewContext } from '../context';
import PageTitle from '../components/PageTitle';

const StyledText = styled(Text)`
  &&& {
    margin-top: 0;
    margin-bottom: 0.5rem;
    ${mediaQueries.desktopUp} {
      margin-bottom: 1rem;
    }
  }
`;

const StyledFormField = styled(FormField)`
  &&& {
    margin-bottom: 2rem;

    label {
      font-weight: ${designTokens.fontWeightBold};
      margin-bottom: 0.5rem;
      font-size: var(--skapa-body-l, 16);
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;

  ${mediaQueries.portraitTabletUp} {
    &&& button {
      width: 50%;
    }
  }
`;

const TopOfPageContainer = styled.div`
  padding-top: 32px;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  ${mediaQueries.portraitTabletUp} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const TextContainer = styled.div`
  width: 100%;
  ${mediaQueries.desktopUp} {
    width: 50%;
  }
`;

const ImageContainer = styled.div`
  margin-top: 1rem;

  img {
    width: 100%;
    height: auto;
  }
  ${mediaQueries.portraitTabletUp} {
    margin-top: 0;
    margin-left: 2rem;

    img {
      max-height: 240px;
    }
  }
`;

const RequiredFieldsNote = styled(Text)`
  &&& {
    margin-bottom: 1rem;
  }
`;

const StyledAsterisk = styled.span`
  color: ${colors.text.error};
  font-weight: bold;
`;

const QuestionsPage: React.FC = () => {
  const view = useContext(ViewContext);
  const { t, i18n } = useTranslation();
  const [hasFileUploadConsent, setHasFileUploadConsent] = useState(false);
  const [
    {
      questions,
      questionsToken,
      answers,
      shouldValidate,
      questionsAnswered,
      showErrorOnSubmit,
      submitCount,
      touchedFields,
      isBusy,
    },
    { changeInputHandler, blurHandler, submitHandler },
  ] = useQuestions(hasFileUploadConsent);

  const hasFileUploadItem = questions.some(
    (question) => question.type === 'FILE',
  );
  const isViewAlt = view === 'alt';
  const isViewApp = view === 'app';

  const CONSENT_FIELD_INDEX = -1;
  const shouldShowConsentError =
    shouldValidate || touchedFields.has(CONSENT_FIELD_INDEX);
  let consentError = '';
  if (shouldShowConsentError) {
    const fileQuestion = questions.find((question) => question.type === 'FILE');
    if (fileQuestion && !hasFileUploadConsent) {
      consentError = VALIDATORS.CONSENT(hasFileUploadConsent);
    }
  }
  const hasConsent = !consentError;

  if (!questionsToken || questionsAnswered) {
    return (
      <Text
        tagName={isViewAlt ? 'h2' : 'h1'}
        textStyle="Heading.M"
        data-cy="questionnaire-not-available"
      >
        {t('pages.questions.questionnaireNotAvailable')}
      </Text>
    );
  }

  const handleInputChange = (
    e: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    > | null,
    index: number,
    questionType: QuestionType,
    files?: FileAnswer[],
  ): void => {
    changeInputHandler(e, index, questionType, files);
  };

  const onFileUploadConsent = () => {
    setHasFileUploadConsent((prev) => !prev);
  };

  const questionsList = questions.map((question: Question, index: number) => {
    const { id: questionId, type: questionType } = question;
    let errorTranslationKey = '';
    const shouldShowError = shouldValidate || touchedFields.has(index);

    if (shouldShowError) {
      if (questionType === 'FILE') {
        errorTranslationKey = VALIDATORS.FILE(
          answers[index].answer as FileAnswer[],
        );
      } else {
        errorTranslationKey = VALIDATORS[questionType](
          answers[index].answer as string[],
        );
      }
    }

    const valid = !errorTranslationKey;

    return (
      <StyledFormField
        key={`question_${question.id}`}
        valid={valid}
        shouldValidate={shouldShowError && !valid}
        characterLimit={questionType === 'TEXT' ? CHARACTER_LIMIT : undefined}
        validation={{
          msg: (
            <div role="alert" aria-live="assertive">
              {t(errorTranslationKey)}
            </div>
          ),
          type: 'error',
          id: `${ERROR_ID[questionType]}-${questionId}`,
        }}
        disabled={isBusy}
      >
        <QuestionsListItem
          question={question}
          onChangeInputHandler={(e, files) =>
            handleInputChange(e, index, question.type, files)
          }
          answer={answers[index].answer}
          onBlur={() => blurHandler(index)}
          disabled={isBusy}
        />
      </StyledFormField>
    );
  });

  return (
    <>
      <PageTitle title={t('pages.questions.pageTitle')} />
      {isViewAlt && <StickyHeader customEventName={'BOKA/QUESTIONS_CLOSED'} />}
      <StyledWrapper $withStickyFooter={isViewAlt || isViewApp}>
        <PageContent>
          <TopOfPageContainer>
            <TextContainer>
              <StyledText
                tagName={isViewAlt ? 'h2' : 'h1'}
                textStyle="Heading.L"
                data-cy="questions-page-title"
              >
                {t('pages.questions.questionsTitle')}
              </StyledText>
              <Text
                tagName="p"
                textStyle="Body.L"
                data-cy="questions-page-description"
              >
                {t('pages.questions.questionsDescription')}
              </Text>
            </TextContainer>
            {isViewAlt || isViewApp ? null : (
              <ImageContainer>
                <img src={coworkerImage} alt="" aria-hidden="true" />
              </ImageContainer>
            )}
          </TopOfPageContainer>
          <form
            noValidate
            data-cy="questions-form"
            id="questionsForm"
            onSubmit={submitHandler}
          >
            {showErrorOnSubmit && (
              <ErrorSummary
                text={t('components.error.questions.answerSummaryError')}
                submitCount={submitCount}
              />
            )}
            <RequiredFieldsNote tagName="p" textStyle="Body.S">
              <Trans
                i18n={i18n}
                i18nKey="pages.questions.requiredFieldsNote"
                components={{ asterisk: <StyledAsterisk /> }}
              />
            </RequiredFieldsNote>
            {questionsList}
            {hasFileUploadItem && (
              <>
                <StyledFormField
                  valid={hasConsent}
                  shouldValidate={shouldShowConsentError && !hasConsent}
                  validation={{
                    msg: (
                      <div role="alert" aria-live="assertive">
                        {t(consentError)}
                      </div>
                    ),
                    type: 'error',
                    id: `${ERROR_ID['FILE']}`,
                  }}
                  disabled={isBusy}
                >
                  <Checkbox
                    id="fileUploadConsent"
                    value="fileUploadConsent"
                    onChange={onFileUploadConsent}
                    checked={!!hasFileUploadConsent}
                    label={t('pages.questions.fileUploadConsentLabel')}
                    required
                    data-cy="file-upload-consent-checkbox"
                    disabled={isBusy}
                    onBlur={() => blurHandler(CONSENT_FIELD_INDEX)}
                  />
                </StyledFormField>
              </>
            )}
          </form>
          {isViewAlt || isViewApp ? (
            <StickyFooter>
              <Button
                htmlType="submit"
                form="questionsForm"
                text={t('pages.questions.submitBtnText')}
                type="emphasised"
                id="submitQuestions"
                disabled={isBusy}
                data-cy="questions-submit-button"
              />
            </StickyFooter>
          ) : (
            <ButtonWrapper>
              <Button
                htmlType="submit"
                form="questionsForm"
                type="emphasised"
                text={t('pages.questions.submitBtnText')}
                fluid
                id="submitQuestions"
                disabled={isBusy}
                data-cy="questions-submit-button"
              />
            </ButtonWrapper>
          )}
        </PageContent>
      </StyledWrapper>
    </>
  );
};

export default QuestionsPage;
